import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { learnerMeApi } from "./_learnerActions";
import { resolveLmsShortName, learnerPath } from "./_learnerConfig";

// ─────────────────────────────────────────────────────────────────────────────
// ESHS LMS — learner auth store, useAuth hook, and route guard.
//
// The access token is mirrored for refresh-safe SPA navigation. The refresh
// token is never exposed to JavaScript: the server stores and rotates it only in
// an HttpOnly cookie. Tokens are never logged.
// ─────────────────────────────────────────────────────────────────────────────

const AUTH_KEY = "ecauthtoken";
const LEARNER_KEY = "lmsLearner";

// In-memory primary store (cleared on full reload; rehydrated from storage).
let memAuth = null;

const readStore = (k) => {
  try {
    return sessionStorage.getItem(k) || localStorage.getItem(k) || null;
  } catch {
    return null;
  }
};

// Seed the access token returned by login into memory and the key read by the
// shared client. Sliding access-token renewals use the same key.
export const persistTokens = (tokens = {}) => {
  const a = tokens.ecAuthToken || tokens.ecauthtoken;
  if (a) {
    memAuth = a;
    try {
      localStorage.setItem(AUTH_KEY, a);
      localStorage.setItem("portalRole", "learner");
    } catch {}
  }
  // The refresh token is delivered only as an HttpOnly cookie.
  try { localStorage.removeItem("ecsecuretoken"); } catch {}
};

export const getAuthToken = () => memAuth || readStore(AUTH_KEY);
export const hasTokens = () => {
  const token = getAuthToken();
  if (!token) return false;
  const role = readStore("portalRole");
  return !role || role === "learner";
};

export const persistLearner = (learner) => {
  try {
    sessionStorage.setItem(LEARNER_KEY, JSON.stringify(learner || null));
  } catch {}
};

export const readLearner = () => {
  try {
    return JSON.parse(sessionStorage.getItem(LEARNER_KEY) || "null");
  } catch {
    return null;
  }
};

export const clearAuth = () => {
  memAuth = null;
  try {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem("ecsecuretoken");
    localStorage.removeItem("portalRole");
    sessionStorage.removeItem(LEARNER_KEY);
  } catch {}
};

/* ───────────── Context + hook ───────────── */

const LearnerAuthContext = createContext(null);

export const LearnerAuthProvider = ({ children }) => {
  const [learner, setLearner] = useState(() => readLearner());

  const login = ({ tokens, learner: who }) => {
    persistTokens(tokens);
    persistLearner(who);
    setLearner(who || null);
  };

  const logout = () => {
    clearAuth();
    setLearner(null);
  };

  const value = {
    learner,
    isAuthenticated: hasTokens(),
    login,
    logout,
    setLearner: (who) => {
      persistLearner(who);
      setLearner(who);
    },
  };

  return (
    <LearnerAuthContext.Provider value={value}>
      {children}
    </LearnerAuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(LearnerAuthContext);
  if (!ctx) {
    // Allow use outside the provider (e.g. a page mounted directly) by falling
    // back to the module-level stores.
    return {
      learner: readLearner(),
      isAuthenticated: hasTokens(),
      login: ({ tokens, learner }) => {
        persistTokens(tokens);
        persistLearner(learner);
      },
      logout: clearAuth,
      setLearner: persistLearner,
    };
  }
  return ctx;
};

/* ───────────── Route guard ───────────── */

// Guards authenticated learner routes. If no tokens are present → login. If
// tokens exist, attempt ONE silent slide via GET /me (which also refreshes the
// sliding tokens through the client interceptor); a 401 there clears auth and
// bounces to login. `redirect` param preserves where the user was headed.
export const LearnerRoute = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState(hasTokens() ? "checking" : "denied");

  useEffect(() => {
    let active = true;
    if (!hasTokens()) {
      setState("denied");
      return;
    }
    (async () => {
      try {
        const res = await learnerMeApi();
        if (!active) return;
        if (res?.data?.status === 200) {
          persistLearner(res?.data?.data?.learner || res?.data?.data);
          setState("allowed");
        } else {
          // Soft envelope (e.g. 204) — treat as an inactive/invalid session.
          clearAuth();
          setState("denied");
        }
      } catch (e) {
        if (!active) return;
        // 401 is already cleared+redirected by the shared client; clear locally
        // too so the guard renders the redirect deterministically.
        clearAuth();
        setState("denied");
      }
    })();
    return () => {
      active = false;
    };
  }, [location.pathname]);

  if (state === "denied") {
    return (
      <Navigate
        to={learnerPath(resolveLmsShortName(), "login")}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (state === "checking") {
    return (
      <div className="lms-auth-guard-loading">
        <i className="fas fa-circle-notch fa-spin mr-2"></i>
      </div>
    );
  }
  return children;
};
